import { Children, Fragment, cloneElement, isValidElement, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactElement, type ReactNode } from 'react'
import { cx } from './cx'
import { balancedColumns } from './cardGridLayout'

export interface CardGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Larghezza minima di colonna in px, default 320 (360 se le card contengono tabelle). */
  min?: number | undefined
  /** Massimo di colonne, default 4. */
  max?: 1 | 2 | 3 | 4 | 6 | undefined
  /** Spazio tra le card: 'collection' 8, 'section' 24 (default). */
  gap?: 'collection' | 'section' | undefined
  'aria-label'?: string | undefined
  children: ReactNode
}

export interface CardGridItemProps extends HTMLAttributes<HTMLDivElement> {
  /** Colonne occupate, ridotte alle colonne disponibili. Default 1. */
  span?: 1 | 2 | 3 | 'full' | undefined
  children: ReactNode
}

/** Figlio di CardGrid con un numero di colonne proprio (MyDashboard: widget s/m/l). */
export function CardGridItem({ span = 1, className, style, ...rest }: CardGridItemProps) {
  // `--_span` (colonne effettive) lo imposta CardGrid, che conosce le colonne; fuori da una CardGrid vale 1.
  return <div {...rest} data-span={span} className={cx('md-card-grid__item', className)} style={style} />
}

const GAPS = { collection: 8, section: 24 } as const

/** Appiattisce i Fragment: i figli presenti sono quelli che contano per il bilanciamento. */
function flatten(children: ReactNode): ReactNode[] {
  const out: ReactNode[] = []
  Children.toArray(children).forEach(c => {
    if (isValidElement(c) && c.type === Fragment) {
      const k = String(c.key)
      flatten((c as ReactElement<{ children?: ReactNode }>).props.children).forEach(n => out.push(isValidElement(n) ? cloneElement(n, { key: `${k}/${String(n.key)}` }) : n))
    } else out.push(c)
  })
  return out
}

function requestedSpan(c: ReactNode): 1 | 2 | 3 | 'full' {
  if (isValidElement(c) && c.type === CardGridItem) return (c as ReactElement<CardGridItemProps>).props.span ?? 1
  return 1
}

/**
 * Collezione di card in una griglia bilanciata (nessuna colonna vuota accanto a una card):
 * - colonne = quelle che ci stanno (`min`, `max`), poi bilanciate sulle righe (5 -> 3+2, 7 -> 4+3);
 * - l'ultima riga si allarga (flex-grow); una card sola occupa tutta la larghezza; niente masonry.
 * La larghezza e' misurata sul contenitore (ResizeObserver), non sul viewport.
 */
export function CardGrid({ min = 320, max = 4, gap = 'section', className, style, children, ...rest }: CardGridProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const measure = () => setWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const g = GAPS[gap]
  const kids = flatten(children)
  const items = kids.map(requestedSpan)
  const cols = width === null ? 1 : balancedColumns(items, width, min, max, g)
  const content = kids.map((c, i) => {
    if (isValidElement(c) && c.type === CardGridItem) {
      const sp = items[i] as 1 | 2 | 3 | 'full'
      const span = sp === 'full' ? cols : Math.min(sp, cols)
      const el = c as ReactElement<CardGridItemProps>
      return cloneElement(el, { style: { ...el.props.style, '--_span': span } as CSSProperties })
    }
    return c
  })
  const Tag = (rest['aria-label'] ? 'section' : 'div') as 'section' | 'div'
  return (
    <Tag
      {...rest}
      ref={ref as never}
      className={cx('md-card-grid', 'md-card-grid__inner', className)}
      style={{ ...style, '--_cols': cols, '--_gap': `${g}px` } as CSSProperties}
    >
      {content}
    </Tag>
  )
}

/** Griglia di KPI: figli = StatCard diretti. min 156, max 6, gap 8. */
export function StatGrid({ min = 156, max = 6, ...rest }: Omit<CardGridProps, 'gap'>) {
  return <CardGrid min={min} max={max} gap="collection" {...rest} />
}
